import React, { useEffect, useRef, useState, memo } from "react";
import "./AboutSection.css";
import { motion } from "framer-motion";
import MarqueeGallery from "./InteractiveGallerySection";

function useCountUp(target, duration = 1500) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);

          const start = 0;
          const end = parseInt(target, 10);
          const increment = end / (duration / 16); // 60fps
          let current = start;

          const timer = setInterval(() => {
            current += increment;
            if (current >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 16);

          return () => clearInterval(timer);
        }
      },
      {
        threshold: 0.3, // Lower threshold for better triggering
        rootMargin: "0px 0px -100px 0px", // Trigger earlier
      }
    );

    const element = elementRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [target, duration, hasStarted]);

  return [count, elementRef];
}

const AboutSection = memo(() => {
  const [countProjects, refProjects] = useCountUp(578, 1500);
  const [countClients, refClients] = useCountUp(50, 1000);
  const [countYears, refYears] = useCountUp(11, 800);
  const [countSatisfaction, refSatisfaction] = useCountUp(100, 1200);

  return (
    <motion.section
      className="about-section"
      id="about"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="about-split">
        <div className="about-left">
          <span className="about-badge">O nas</span>
          <h2>
            Smo strokovnjaki za izdelavo in montažo kletnih shramb in predelnih
            sten.
          </h2>
        </div>
        <div className="about-right">
          <div className="about-desc">
            <p>
              Naš poudarek je na najkvalitetnejših materialih, natančni izvedbi
              in individualnem pristopu do vsake stranke.
            </p>
            <p>Z dolgoletnimi izkušnjami zagotavljamo:</p>
            <ul className="about-list">
              <li>Varnost in funkcionalnost</li>
              <li>Trajnost vaših prostorov</li>
              <li>Strokovno svetovanje in načrtovanje</li>
              <li>Prilagodljivost vašim potrebam</li>
              <li>Modularno gradnjo za prihodnje razširitve</li>
              <li>Celovito projektno podporo od načrta do montaže</li>
              <li>Kakovostne materiale vodilnih proizvajalcev</li>
              <li>Pravočasno in zanesljivo izvedbo</li>
            </ul>
          </div>
        </div>
      </div>
      <MarqueeGallery />
      <div className="about-stats">
        <div className="about-stat" ref={refProjects}>
          <div className="about-stat-number">{countProjects}+</div>
          <div className="about-stat-title">Zaključenih projektov</div>
          <div className="about-stat-desc">
            Več kot 578 uspešno zaključenih projektov z vrhunsko kakovostjo
          </div>
        </div>
        <div className="about-stat" ref={refClients}>
          <div className="about-stat-number">{countClients}+</div>
          <div className="about-stat-title">Zadovoljnih strank</div>
          <div className="about-stat-desc">
            Več kot 50 zadovoljnih strank, ki nam zaupajo
          </div>
        </div>
        <div className="about-stat" ref={refYears}>
          <div className="about-stat-number">{countYears}</div>
          <div className="about-stat-title">Let delovanja</div>
          <div className="about-stat-desc">
            Že 11 let ustvarjamo kakovostne rešitve
          </div>
        </div>
        <div className="about-stat" ref={refSatisfaction}>
          <div className="about-stat-number">{countSatisfaction}%</div>
          <div className="about-stat-title">Zadovoljstvo strank</div>
          <div className="about-stat-desc">
            Vse naše stranke so zadovoljne z našim delom in storitvijo
          </div>
        </div>
      </div>
    </motion.section>
  );
});

AboutSection.displayName = "AboutSection";

export default AboutSection;
